"use client";

import { BarChart } from "@/components/charts/bar-chart";
import { Bar } from "@/components/charts/bar";
import { BarXAxis } from "@/components/charts/bar-x-axis";
import { Grid } from "@/components/charts/grid";
import { YAxis } from "@/components/charts/y-axis";
import { ChartTooltip } from "@/components/charts/tooltip";
import { ReferenceArea } from "@/components/charts/reference-area";
import { ReferenceAreaLabel } from "@/components/charts/reference-area-label";
import { ChartBrush, ChartBrushLayout } from "@/components/charts/chart-brush";
import type { ChartBrushLayoutRenderProps } from "@/components/charts/chart-brush";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import {
  ChartAxisFrame,
  ChartXAxisRow,
} from "@/components/charts/chart-axis-frame";
import { ChartLegendHoverProvider } from "@/components/charts/chart-legend-hover";
import { ProjectionFadeOut } from "@/components/charts/bar-projection-context";
import {
  type ChartView,
  ChartViewToggle,
  chartViewToMode,
} from "@/components/charts/projection/chart-view-toggle";
import { MAX_PROJECTED_BOXES } from "@/components/charts/projection/iso-projection";
import { useLegendClickSync } from "@/components/charts/use-legend-click-sync";
import {
  Legend,
  LegendItem,
  LegendLabel,
  LegendMarker,
} from "@/components/charts/legend";
import { cn } from "@/lib/utils";
import {
  ChartDataPoint,
  ChartThreshold,
  type ModuleChartSeriesMeta,
  type ModuleReferenceArea,
} from "@/types/modules";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import React, { JSX, useMemo, useState } from "react";
import {
  moduleAxisLabels,
} from "./chart-module-metrics";
import { CHART_PLOT_HEIGHT, MIN_CHART_WIDTH, MIN_PX_PER_POINT } from "@/config/layout";
import { accentChartColors } from "../module-accent";
import { chartPaletteStyle } from "../imports-token-scope";

const CHART_HEIGHT = CHART_PLOT_HEIGHT.bar;
const PX_PER_POINT = MIN_PX_PER_POINT.bar;

export default function ModuleBarChart({
  data,
  xAxisKey = "name",
  xAxisLabel,
  yAxisLabel,
  thresholds,
  series,
  showBrush = false,
  showGrid = true,
  showLegend,
  variant = "bars",
  view3d,
  referenceArea,
  grayscale,
  accent,
  className,
}: {
  data?: ChartDataPoint[];
  xAxisKey?: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  thresholds?: ChartThreshold[];
  series?: ModuleChartSeriesMeta[];
  showBrush?: boolean;
  showGrid?: boolean;
  showMarkers?: boolean;
  showLegend?: boolean;
  fadeEdges?: boolean;
  variant?: "bars" | "squares";
  view3d?: boolean;
  referenceArea?: ModuleReferenceArea;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const palette = accentChartColors(grayscale, accent);
  const dataKeys = React.useMemo(() => {
    if (series && series.length > 0) return series.map((s) => s.key);
    if (!data || data.length === 0) return [];
    const firstItem = data[0];
    if (!firstItem) return [];
    return Object.keys(firstItem).filter((key) => key !== xAxisKey);
  }, [data, xAxisKey, series]);

  const seriesColor = (key: string, index: number) => {
    const meta = series?.find((s) => s.key === key);
    return meta?.color ?? palette[index % palette.length] ?? "#000";
  };
  const seriesLabel = (key: string) => {
    const meta = series?.find((s) => s.key === key);
    return stripInlineMarkdown(meta?.label) || key;
  };

  const yThresholds = useMemo(
    () => (thresholds ?? []).filter((t) => t.axis !== "x"),
    [thresholds],
  );

  const source = useMemo(() => data ?? [], [data]);

  const legendVisible = showLegend ?? dataKeys.length > 1;
  void variant;

  const canProject =
    Boolean(view3d) &&
    source.length * Math.max(1, dataKeys.length) <= MAX_PROJECTED_BOXES;
  const [view, setView] = useState<ChartView>("3d");
  const projection = canProject ? chartViewToMode(view) : undefined;
  const brushDisabled = projection === "isometric";

  const [legendHovered, setLegendHovered] = useState<number | null>(null);
  const {
    activeIndex,
    handleSelect,
    setLabelRef,
    selectedOrigin,
    legendSelectedIndex,
    elementIndex,
    toggleElementIndex,
  } = useLegendClickSync();

  return (
    <ChartLegendHoverProvider
      hoveredIndex={legendHovered}
      onHoverChange={setLegendHovered}
      selectedIndex={activeIndex}
      selectedOrigin={selectedOrigin}
      elementIndex={elementIndex}
      onElementToggle={toggleElementIndex}
    >
    <ChartBrushLayout
      data={source as unknown as Record<string, unknown>[]}
      xDataKey={xAxisKey}
    >
      {(layout: ChartBrushLayoutRenderProps<Record<string, unknown>>) => {
        const visibleData = (layout.filteredData as typeof source).map((row) => {
          const raw = row as Record<string, unknown>;
          const val = raw[xAxisKey];
          return typeof val === "string"
            ? { ...raw, [xAxisKey]: stripInlineMarkdown(val) }
            : raw;
        }) as typeof source;
        const brush = (
          <ChartBrush
            data={source as unknown as Record<string, unknown>[]}
            xDataKey={xAxisKey}
            variant="band"
            height={36}
            onSelectionChange={layout.onBrushSelectionChange}
            initialSelection={layout.brushSelection ?? undefined}
            previewSeries={dataKeys.map((key, i) => ({
              dataKey: key,
              color: seriesColor(key, i),
            }))}
          />
        );
        return (
    <div className={cn("flex w-full flex-col gap-3", className)} style={chartPaletteStyle(grayscale, accent)}>
      {canProject ? (
        <div className="flex h-8 items-center justify-end">
          <ChartViewToggle
            className="shrink-0"
            kind="bar"
            onChange={setView}
            value={view}
          />
        </div>
      ) : null}
      <ChartAxisFrame {...moduleAxisLabels({ xAxisLabel, yAxisLabel })}>
      <SwipeScroll className="min-w-0 flex-1 pb-1 [mask-image:linear-gradient(to_right,black_0%,black_95%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_right,black_0%,black_95%,transparent_100%)]">
      <div
        style={{
          minWidth: Math.max(MIN_CHART_WIDTH, visibleData.length * PX_PER_POINT),
        }}
      >
      <div style={{ height: CHART_HEIGHT }}>
        <BarChart
          data={visibleData}
          xDataKey={xAxisKey}
          margin={{ top: 10, right: 24, bottom: 8, left: 0 }}
          aspectRatio="auto"
          className="h-full"
          projection={projection}
        >
          {showGrid && (
            <Grid
              horizontal
              vertical={false}
              highlightRowValues={
                yThresholds.length > 0
                  ? [0, ...yThresholds.map((t) => t.value)]
                  : [0]
              }
              highlightRowStroke={yThresholds[0]?.color ?? "rgb(115 115 115)"}
              highlightRowStrokeDasharray="4,4"
              highlightRowStrokeWidth={1.5}
            />
          )}
          {referenceArea && (
            <>
              <ReferenceArea
                y1={referenceArea.y1}
                y2={referenceArea.y2}
                fill={referenceArea.color ?? "var(--chart-1)"}
                fillOpacity={0.15}
                stroke={referenceArea.color ?? "var(--chart-1)"}
                strokeStyle="dashed"
                strokeWidth={1.5}
                fadeEdges
                fadeEdgesLength={12}
                pattern={
                  referenceArea.variant === "pattern"
                    ? (referenceArea.pattern ?? "diagonal")
                    : "none"
                }
              />
              {referenceArea.label &&
                (canProject ? (
                  <ProjectionFadeOut>
                    <ReferenceAreaLabel
                      y2={referenceArea.y2}
                      label={stripInlineMarkdown(referenceArea.label)}
                    />
                  </ProjectionFadeOut>
                ) : (
                  <ReferenceAreaLabel
                    y2={referenceArea.y2}
                    label={stripInlineMarkdown(referenceArea.label)}
                  />
                ))}
            </>
          )}
          {dataKeys.map((key, index) => (
            <Bar
              key={key}
              dataKey={key}
              fill={seriesColor(key, index)}
              label={seriesLabel(key)}
              lineCap="round"
            />
          ))}
          <BarXAxis />
          <YAxis />
          <ChartTooltip
            showDatePill
            rows={(point) =>
              dataKeys.map((key, i) => ({
                color: seriesColor(key, i),
                label: seriesLabel(key),
                value: Number(point[key] ?? 0),
              }))
            }
          />
        </BarChart>
      </div>
      <ChartXAxisRow />
      </div>
      </SwipeScroll>
      </ChartAxisFrame>

      {showBrush && source.length > 0 && (
        canProject ? (
          <div
            aria-disabled={brushDisabled || undefined}
            className={cn(
              "transition-opacity duration-300",
              brushDisabled && "pointer-events-none opacity-40"
            )}
          >
            {brush}
          </div>
        ) : (
          brush
        )
      )}

      {legendVisible && (
        <SwipeScroll className="w-full pb-1" fade="auto">
          <Legend
            items={dataKeys.map((key, i) => ({
              label: seriesLabel(key),
              value: data?.length ?? 0,
              color: seriesColor(key, i),
            }))}
            hoveredIndex={legendHovered}
            onHoverChange={setLegendHovered}
            selectedIndex={legendSelectedIndex}
            onSelect={handleSelect}
            onLabelRef={setLabelRef}
            className="flex flex-row gap-6"
          >
            <LegendItem className="flex shrink-0 items-center gap-2">
              <LegendMarker className="h-2.5 w-2.5" />
              <LegendLabel />
            </LegendItem>
          </Legend>
        </SwipeScroll>
      )}
    </div>
        );
      }}
    </ChartBrushLayout>
    </ChartLegendHoverProvider>
  );
}
